import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import SEO from "../../components/SEO";

const inputCls = "w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm";
const btnCls = "w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2";

function Shell({ title, sub, children, testid }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4 py-16">
      <div data-testid={testid} className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-primary-foreground" /></span>
          <span className="font-heading font-bold">Tendly Staff</span>
        </div>
        <h1 className="font-heading font-bold text-2xl tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export function StaffLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [phase, setPhase] = useState("credentials"); // credentials | setup | verify
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);

  if (user && ["admin", "agent"].includes(user.role)) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const afterAuth = (u) => {
    setUser(u);
    navigate(["admin", "agent"].includes(u.role) ? "/dashboard" : "/account", { replace: true });
  };

  const submitCredentials = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/login", form);
      const d = r.data;
      if (d.requires_2fa_setup) {
        setPending(d.pending_token);
        const s = await api.post("/auth/2fa/setup", { pending_token: d.pending_token });
        setSetup(s.data);
        setPhase("setup");
      } else if (d.requires_2fa) {
        setPending(d.pending_token);
        setPhase("verify");
      } else {
        afterAuth(d);
      }
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const submitCode = async () => {
    setBusy(true);
    try {
      const path = phase === "setup" ? "/auth/2fa/enable" : "/auth/2fa/verify";
      const r = await api.post(path, { pending_token: pending, code, remember_device: remember });
      afterAuth(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  if (phase === "setup" && setup) {
    return (
      <Shell title="Set up two-factor authentication" sub="Scan the QR code with your authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit code." testid="staff-2fa-setup">
        <SEO title="Staff sign in — Tendly" />
        <div className="space-y-4">
          <div className="flex justify-center rounded-xl border border-border bg-white p-4">
            <img data-testid="staff-2fa-qr" src={`data:image/png;base64,${setup.qr_png_base64}`} alt="2FA QR code" className="w-44 h-44" />
          </div>
          <p className="text-xs text-muted-foreground text-center">Can't scan? Enter this key manually: <code data-testid="staff-2fa-secret" className="font-mono font-semibold text-foreground break-all">{setup.secret}</code></p>
          <input data-testid="staff-2fa-code" inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" aria-label="Authenticator code"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${inputCls} text-center tracking-[0.3em] font-mono`}
            onKeyDown={(e) => e.key === "Enter" && code.length === 6 && submitCode()} />
          <label data-testid="staff-2fa-remember" className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-primary w-3.5 h-3.5" />
            Remember this device — don't ask for a code again for 60 days
          </label>
          <button data-testid="staff-2fa-submit" onClick={submitCode} disabled={busy || code.length !== 6} className={btnCls}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enable and sign in
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === "verify") {
    return (
      <Shell title="Two-factor authentication" sub="Enter the 6-digit code from your authenticator app." testid="staff-2fa-verify">
        <SEO title="Staff sign in — Tendly" />
        <div className="space-y-3">
          <input data-testid="staff-2fa-code" inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" aria-label="Authenticator code"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${inputCls} text-center tracking-[0.3em] font-mono`}
            onKeyDown={(e) => e.key === "Enter" && code.length === 6 && submitCode()} autoFocus />
          <label data-testid="staff-2fa-remember" className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-primary w-3.5 h-3.5" />
            Remember this device — don't ask for a code again for 60 days
          </label>
          <button data-testid="staff-2fa-submit" onClick={submitCode} disabled={busy || code.length !== 6} className={btnCls}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify and sign in
          </button>
          <p className="text-xs text-center"><button data-testid="staff-2fa-back" onClick={() => { setPhase("credentials"); setCode(""); setPending(null); }} className="text-primary hover:underline">Back</button></p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Staff sign in" sub="Authorized Tendly staff only." testid="staff-login-card">
      <SEO title="Staff sign in — Tendly" />
      <div className="space-y-3">
        <input data-testid="staff-login-email" type="email" placeholder="Email" aria-label="Email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <input data-testid="staff-login-password" type="password" placeholder="Password" aria-label="Password" value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls}
          onKeyDown={(e) => e.key === "Enter" && submitCredentials()} />
        <button data-testid="staff-login-submit" onClick={submitCredentials} disabled={busy} className={btnCls}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
        </button>
        <p className="text-xs text-center"><Link to="/staff/forgot" data-testid="staff-forgot-link" className="text-primary hover:underline">Forgot password?</Link></p>
      </div>
    </Shell>
  );
}

export function StaffForgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setDone(true); }
    catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };
  return (
    <Shell title="Reset your password" sub="We'll email a secure reset link to your staff account email." testid="staff-forgot-card">
      <SEO title="Staff password reset — Tendly" />
      {done ? (
        <p data-testid="staff-forgot-done" className="rounded-xl bg-secondary px-4 py-3 text-sm">If that email is registered, a reset link has been sent.</p>
      ) : (
        <div className="space-y-3">
          <input data-testid="staff-forgot-email" type="email" placeholder="Email" aria-label="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} className={inputCls} onKeyDown={(e) => e.key === "Enter" && submit()} />
          <button data-testid="staff-forgot-submit" onClick={submit} disabled={busy} className={btnCls}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
          </button>
        </div>
      )}
      <p className="mt-4 text-xs text-center"><Link to="/staff" data-testid="staff-forgot-back" className="text-primary hover:underline">Back to sign in</Link></p>
    </Shell>
  );
}

export function StaffReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/staff");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };
  return (
    <Shell title="Choose a new password" sub="At least 8 characters." testid="staff-reset-card">
      <SEO title="Set password — Tendly Staff" />
      <div className="space-y-3">
        <input data-testid="staff-reset-password-input" type="password" placeholder="New password" aria-label="New password" value={pw}
          onChange={(e) => setPw(e.target.value)} className={inputCls} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <button data-testid="staff-reset-submit" onClick={submit} disabled={busy} className={btnCls}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
        </button>
        <p className="text-xs text-center"><Link to="/staff" className="text-primary hover:underline">Back to sign in</Link></p>
      </div>
    </Shell>
  );
}
